import { DatabaseSelection } from '@blocksuite/affine/blocks/database';
import {
  type BaseSelection,
  type Store,
  StoreSelectionExtension,
} from '@blocksuite/affine/store';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';

import { DocsService } from '../../doc';
import { WorkbenchService } from '../../workbench/services/workbench';

/** The rows of the source table the user chose for a manual run. */
export interface ChosenSourceRows {
  rowIds: string[];
  /** The document of the source table is open, so its rows can be chosen. */
  tableOpen: boolean;
}

const NONE: ChosenSourceRows = { rowIds: [], tableOpen: false };

/**
 * The rows chosen with the row checkboxes of the table `tableId` in the
 * selections of its document (every editor over the same store shares them).
 */
export function chosenRowIds(
  selections: readonly BaseSelection[],
  tableId: string
): string[] {
  const rowIds = new Set<string>();
  for (const selection of selections) {
    if (!(selection instanceof DatabaseSelection)) continue;
    if (selection.blockId !== tableId) continue;
    const view = selection.viewSelection;
    if (view.type !== 'table' || view.selectionType !== 'row') continue;
    for (const row of view.rows) rowIds.add(row.id);
  }
  return [...rowIds];
}

/**
 * Follows the rows chosen in the source table of a manual run. A table in
 * another document counts once that document is open in this workspace.
 */
export function useChosenSourceRows(input: {
  enabled: boolean;
  sourceTableId: string | null | undefined;
  sourceDocId: string | null | undefined;
  currentDoc: Store | null | undefined;
}): ChosenSourceRows {
  const { enabled, sourceTableId, sourceDocId, currentDoc } = input;
  const docsService = useService(DocsService);
  const workbench = useService(WorkbenchService).workbench;
  // Opening or switching a document can load the source table's document.
  const activeView = useLiveData(workbench.activeView$);
  const location = useLiveData(activeView.location$);
  const [chosen, setChosen] = useState<ChosenSourceRows>(NONE);

  useEffect(() => {
    if (!enabled || !sourceTableId || !currentDoc) {
      setChosen(NONE);
      return;
    }
    const crossDoc = !!sourceDocId && sourceDocId !== currentDoc.id;
    const loaded = crossDoc ? docsService.loaded(sourceDocId) : null;
    const store = crossDoc ? loaded?.doc.blockSuiteDoc : currentDoc;
    if (!store) {
      setChosen(NONE);
      return;
    }
    const selection = store.provider.get(StoreSelectionExtension);
    const read = () =>
      setChosen({
        rowIds: chosenRowIds(selection.value, sourceTableId),
        tableOpen: true,
      });
    read();
    const subscription = selection.slots.changed.subscribe(read);
    return () => {
      subscription.unsubscribe();
      loaded?.release();
    };
  }, [enabled, sourceTableId, sourceDocId, currentDoc, docsService, location]);

  return chosen;
}
